// ─────────────────────────────────────────────────────────────────────────────
// deltaNeedsRun.ts — WHO NEEDS A RUN, TODAY'S TRUTH, AND THE ONE PRESS.
//
// `GET /api/delta/needs-run` · `POST /api/delta/run-unscripted`. Forge owns both
// (owner ruling 2026-09-28). The list is qualified, not parked, no current v2
// script — new companies first — and `today` is the store's count, never the last
// loop's arming. Composed by the board producer; the route never composes.
//
// Same rules as `deltaRunner`: never invent a value · a failed read is UNREAD, not
// empty · a refusal is an answer and its sentence reaches the screen.
// ─────────────────────────────────────────────────────────────────────────────

import { apiBase, authToken } from './apiBase';

export const DELTA_NEEDS_RUN_PATH = '/delta/needs-run';
export const DELTA_RUN_UNSCRIPTED_PATH = '/delta/run-unscripted';

export interface NeedsRunCompany {
  companyId: string;
  business: string | null;
  /** 'none' — a new company; 'v1' — an old script a Run replaces. */
  script: 'none' | 'v1';
  runnable: boolean;
  /** Why a listed company cannot be Run ("need a website"), else null. */
  whyNot: string | null;
  /** The producer's Fit flag ("Fit: Low") — shown, never a gate (2026-10-06). */
  fitFlag?: string | null;
}

export interface DeltaToday {
  v2Scripts: number;
  thin: number;
  onCallList: number;
  /** Runnable with no script at all — the button's count. */
  needARun: number;
  v1ToReplace: number;
}

export type NeedsRunPhase = 'unread' | 'absent' | 'read';

export interface NeedsRunState {
  phase: NeedsRunPhase;
  companies: NeedsRunCompany[];
  today: DeltaToday | null;
  reason: string | null;
}

export const NEEDS_RUN_UNREAD: NeedsRunState = { phase: 'unread', companies: [], today: null, reason: null };

function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/** THE PAYLOAD, OR UNREAD. A today block missing any number is not a today. */
export function normalizeNeedsRun(body: any): NeedsRunState {
  const nr = body && body.needs_run;
  if (!body || body.success !== true) return NEEDS_RUN_UNREAD;
  if (nr === null || nr === undefined) {
    return { phase: 'absent', companies: [], today: null,
             reason: typeof body.reason === 'string' ? body.reason : null };
  }
  const companies: NeedsRunCompany[] = (Array.isArray(nr.companies) ? nr.companies : [])
    .filter((c: any) => c && typeof c.company_id === 'string' && c.company_id)
    .map((c: any) => ({
      companyId: c.company_id,
      business: typeof c.business === 'string' && c.business ? c.business : null,
      script: c.script === 'v1' ? 'v1' : 'none',
      runnable: c.runnable === true,
      whyNot: typeof c.why_not === 'string' && c.why_not ? c.why_not : null,
      fitFlag: typeof c.fit_flag === 'string' && c.fit_flag ? c.fit_flag : null,
    }));
  const t = nr.today || {};
  const fields = [num(t.v2_scripts), num(t.thin), num(t.on_call_list), num(t.need_a_run), num(t.v1_to_replace)];
  const today = fields.every((f) => f !== null)
    ? { v2Scripts: fields[0]!, thin: fields[1]!, onCallList: fields[2]!, needARun: fields[3]!, v1ToReplace: fields[4]! }
    : null;
  return { phase: 'read', companies, today, reason: typeof nr.reason === 'string' ? nr.reason : null };
}

export async function readNeedsRun(): Promise<NeedsRunState> {
  try {
    const res = await fetch(apiBase() + DELTA_NEEDS_RUN_PATH, {
      headers: { Authorization: 'Bearer ' + authToken() },
    });
    if (res.status === 404) return { ...NEEDS_RUN_UNREAD, phase: 'absent', reason: 'not in this build' };
    if (!res.ok) return NEEDS_RUN_UNREAD;
    return normalizeNeedsRun(await res.json());
  } catch {
    return NEEDS_RUN_UNREAD;
  }
}

/** The companies the button runs: runnable and no script at all, in list order. */
export function unscripted(state: NeedsRunState): NeedsRunCompany[] {
  return state.companies.filter((c) => c.runnable && c.script === 'none');
}

export interface RunUnscriptedAnswer {
  ok: boolean;
  started: string[];
  leftOut: { companyId: string; why: string }[];
  /** The route's own sentence when it refused (409) or failed. */
  refused: string | null;
}

export async function runUnscripted(companyIds: string[]): Promise<RunUnscriptedAnswer> {
  let res: Response;
  try {
    res = await fetch(apiBase() + DELTA_RUN_UNSCRIPTED_PATH, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + authToken() },
      body: JSON.stringify({ company_ids: companyIds }),
    });
  } catch {
    return { ok: false, started: [], leftOut: [], refused: 'not sent' };
  }
  let body: any = null;
  try { body = await res.json(); } catch { body = null; }
  const refused = body && (typeof body.refused === 'string' ? body.refused
    : typeof body.error === 'string' ? body.error : null);
  if (!res.ok || !body || body.success !== true) {
    return { ok: false, started: [], leftOut: [], refused: refused || 'refused' };
  }
  return {
    ok: true,
    started: Array.isArray(body.started) ? body.started.filter((c: any) => typeof c === 'string') : [],
    leftOut: (Array.isArray(body.left_out) ? body.left_out : [])
      .filter((r: any) => r && typeof r.company_id === 'string')
      .map((r: any) => ({ companyId: r.company_id, why: String(r.why || '') })),
    refused: null,
  };
}
